import { HttpResourceRequest } from '@angular/common/http';
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { BaseComponent } from './modules/base/base/base.component';
import { API_ENDPOINT } from './constants/api-endpoint.constants';
import { ApiService } from './services/api.service';
import { ClientService } from './services/client.service';
import { DataShareService } from './services/data-share.service';
import { ThemeService } from './services/theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet, BaseComponent]
})
export class AppComponent {
  private readonly clientService = inject(ClientService);
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);
  private readonly swUpdate = inject(SwUpdate);
  private readonly dataShare = inject(DataShareService);
  private readonly themeService = inject(ThemeService);

  private url = signal<string>(this.router.url);
  private routerEvents = toSignal(this.router.events);
  private versionUpdates = toSignal<VersionEvent>(this.swUpdate.versionUpdates);
  protected isNonBaseRoute = computed<boolean>(() => {
    const path = this.url().split('?')[0] || '/';
    return path === '/' || path.startsWith('/redirect');
  });
  protected autoLogin = this.apiService.apiSignal(() => {
    const route = window.location.pathname;
    const redirect = route.startsWith('/redirect');

    if (route && route !== '/' && !route.includes('/redirectUrl=') && !redirect) {
      const redirectUrl = route + window.location.search + window.location.hash;
      window.location.href = window.location.origin + '/?redirectUrl=' + encodeURIComponent(redirectUrl);
    }

    let data: HttpResourceRequest;

    if (!redirect) {
      data = {
        url: API_ENDPOINT.AUTOLOGIN,
        body: {
          timezoneOffset: new Date().getTimezoneOffset()
        }
      };
    }

    return data;
  }, (response: any) => {
    if (response) this.clientService.setLoginData(response);
  });

  constructor() {
    this.themeService.initializeTheme();

    if (this.swUpdate.isEnabled) {
      effect(() => {
        if (this.versionUpdates()?.type === 'VERSION_READY') {
          this.dataShare.versionUpdateAvailable.set(true);
        }
      });
    }
    effect(() => {
      const routerEvents = this.routerEvents();
      if (routerEvents && routerEvents instanceof NavigationEnd) {
        this.url.set(routerEvents.urlAfterRedirects ?? routerEvents.url);
      }
    });
  }
}
